/**
 * "Procurar atualizações" on the web (`D187`, amended by `D219`): the same
 * card the Android app has, over the service worker instead of the OTA
 * updater. Web build only — `webUpdatesSupported()` is `false` in the shell
 * (`isNative()`, and `--mode native` has no worker at all, `D167`).
 *
 * - CHECK asks the registration to fetch the worker script again
 *   (`registration.update()`). A changed script installs beside the running
 *   one and WAITS (`registerType: "prompt"`), so the check waits for that
 *   install and answers `ready` when a worker is waiting, `current` when none
 *   is. A worker already waiting — the toast was missed — is `ready` at once.
 * - APPLY is vite-plugin-pwa's own `updateSW(true)`, handed over by
 *   `register.ts`: it tells the waiting worker to take over and reloads once
 *   it controls the page. Without it (the registration came from an older
 *   tab) the waiting worker is told directly, and the page reloads when the
 *   controller changes.
 * - While the app is open, the registration is re-checked whenever it comes
 *   back to the foreground, at most every {@link RECHECK_MS}: a tab or an
 *   installed PWA left open for days otherwise never hears of a release,
 *   because the browser checks only on navigation.
 *
 * The workout rule is the card's (`app-version.tsx`): during a session the
 * dialog does not offer the reload.
 */

import type { ManualUpdateCheck } from "../../native";
import { isNative } from "../../native";

/** How long a found worker may take to install before the check gives up. */
const INSTALL_TIMEOUT_MS = 30_000;
/** The least time between two background checks. */
const RECHECK_MS = 30 * 60_000;

let applyWaiting: (() => Promise<void>) | null = null;
let lastCheckAt = 0;

/** `register.ts` hands over vite-plugin-pwa's `updateSW`. */
export function setWebUpdater(
  update: (reload?: boolean) => Promise<void>
): void {
  applyWaiting = () => update(true);
}

export function webUpdatesSupported(): boolean {
  return (
    import.meta.env.MODE === "web" &&
    !isNative() &&
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator
  );
}

/** Resolves once the worker has installed (or failed to), or on timeout. */
function settled(worker: ServiceWorker): Promise<void> {
  return new Promise((resolve) => {
    if (worker.state !== "installing") {
      resolve();
      return;
    }
    const timer = window.setTimeout(done, INSTALL_TIMEOUT_MS);
    function done() {
      window.clearTimeout(timer);
      worker.removeEventListener("statechange", onState);
      resolve();
    }
    function onState() {
      if (worker.state !== "installing") {
        done();
      }
    }
    worker.addEventListener("statechange", onState);
  });
}

/**
 * The person asked. `ready` carries no version: the waiting worker does not
 * say which release it is, so the card words it without one.
 */
export async function checkWebUpdateNow(): Promise<ManualUpdateCheck> {
  if (!webUpdatesSupported()) {
    return { kind: "unsupported" };
  }
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration === undefined) {
      return { kind: "failed" };
    }
    if (registration.waiting === null) {
      lastCheckAt = Date.now();
      await registration.update();
      // Read after the update: a found worker is installing only now.
      const { installing } = registration;
      if (installing !== null) {
        await settled(installing);
      }
    }
    return registration.waiting === null
      ? { kind: "current" }
      : { kind: "ready", version: "" };
  } catch {
    return { kind: "failed" };
  }
}

/** Switch to the waiting version; the page reloads into it. */
export async function applyWebUpdate(): Promise<void> {
  if (applyWaiting !== null) {
    await applyWaiting();
    return;
  }
  const registration = await navigator.serviceWorker.getRegistration();
  const waiting = registration?.waiting ?? null;
  if (waiting === null) {
    throw new Error("no waiting service worker");
  }
  navigator.serviceWorker.addEventListener(
    "controllerchange",
    () => window.location.reload(),
    { once: true }
  );
  waiting.postMessage({ type: "SKIP_WAITING" });
}

/**
 * Re-check whenever the page comes back to the foreground, at most every
 * {@link RECHECK_MS}. A found version raises the ordinary prompt through
 * `onNeedRefresh`, so nothing else is needed here.
 */
export function recheckOnReturn(registration: ServiceWorkerRegistration): void {
  lastCheckAt = Date.now();
  document.addEventListener("visibilitychange", () => {
    if (
      document.visibilityState !== "visible" ||
      Date.now() - lastCheckAt < RECHECK_MS
    ) {
      return;
    }
    lastCheckAt = Date.now();
    registration.update().catch(() => undefined);
  });
}
